import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { businessTimeZone } from './businessTime'

/**
 * Merges Tailwind CSS classes with proper precedence handling.
 * Uses clsx for conditional classes and tailwind-merge for deduplication.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Formats a date string into a human-readable relative time.
 */
export function timeAgo(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`
  return date.toLocaleDateString('en-US', { timeZone: businessTimeZone(), month: 'short', day: 'numeric' })
}

/**
 * Formats a date string into a readable format.
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('en-US', {
    timeZone: businessTimeZone(),
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Returns the appropriate color class for a severity level.
 */
export function getSeverityColor(severity: number): string {
  if (severity >= 9) return 'bg-danger text-white'
  if (severity >= 7) return 'bg-warning text-navy'
  if (severity >= 5) return 'bg-yellow-400 text-navy'
  if (severity >= 3) return 'bg-info text-white'
  return 'bg-gray-400 text-white'
}

/**
 * Returns the label for a severity level.
 */
export function getSeverityLabel(severity: number): string {
  if (severity >= 9) return 'Critical'
  if (severity >= 7) return 'High'
  if (severity >= 5) return 'Medium'
  if (severity >= 3) return 'Low'
  return 'Info'
}
